import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { TextTypeEnum } from 'generated/graphql';
import type {
    PageBuilderModuleMetaProps,
    TextColumnProps,
} from '@modules/page-builder/types';
import RichTextDisplay from '../../common/rich-text/display/RichTextDisplay';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';

interface SingleParagraphModuleProps extends PageBuilderModuleMetaProps {
    column: TextColumnProps;
    /**
     * The text type (paragraph, h3, quote). If not provided, gets it from the
     * column properties.
     */
    textType?: TextTypeEnum;
    isNewDesign?: boolean;
    moduleType?: string;
    /** Project detail revamp (BC-4725) — body copy uses the Text font, not Display. */
    isProjectRevamp?: boolean;
}

/**
 * A rendered Page Builder module for a single paragraph. Can be a paragraph,
 * heading, or quote.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/single-paragraph-module/SingleParagraphModule.tsx}
 */
const SingleParagraphModule = ({
    column,
    meta,
    textType,
    isNewDesign = false,
    moduleType,
    isProjectRevamp = false,
}: SingleParagraphModuleProps) => {
    const derivedTextType = useMemo(
        () => textType ?? column.textType ?? TextTypeEnum.Paragraph,
        [textType, column.textType],
    );

    const clickProxy = useWebsiteTrackingProxy({
        professionalID: meta?.professional?.id,
        professionalTitle: meta?.professional?.title,
        itemType: meta?.itemType,
    });

    // Project detail revamp (BC-4725): body copy uses the Text font (font-sans)
    // to match the rest of the page — the CMS rich text often renders as divs,
    // which escape the gallery section's `[&_p]:font-sans!` override, so set it
    // on the wrapper. Headings (H3) keep the Display font.
    const revampBody = isProjectRevamp && derivedTextType !== TextTypeEnum.H3;
    const fontClass = revampBody ? 'font-sans' : 'font-display';

    const className =
        isNewDesign && moduleType !== 'FeaturedProductModule'
            ? cn(
                  'mx-auto w-full px-4.5 text-[1.375rem]! leading-[130%] font-normal tracking-normal text-[#161616] md:max-w-225 [&>div]:text-[1.375rem]! [&>div]:leading-[130%]! [&>div]:tracking-normal!',
                  fontClass,
                  derivedTextType === TextTypeEnum.H3 && 'md:max-w-310',
              )
            : cn(
                  'mx-auto w-full px-4.5 text-[1.375rem]! leading-[130%] font-[450] tracking-normal text-[#161616] md:max-w-225 [&>div]:text-[1.375rem]! [&>div]:leading-[130%]! [&>div]:tracking-normal!',
                  fontClass,
                  derivedTextType === TextTypeEnum.H3 && 'md:max-w-310',
                  moduleType === 'FeaturedProductModule' &&
                      'mx-0 w-fit md:max-w-138',
              );

    return (
        <div
            className={className}
            onClick={clickProxy}
            onContextMenu={clickProxy}
        >
            <RichTextDisplay contentHTML={column.contentHTML} />
        </div>
    );
};

export default SingleParagraphModule;
